// Gutenberg block for WebAsk Integration
const { registerBlockType } = wp.blocks;
const { SelectControl, TextControl, PanelBody } = wp.components;
const { InspectorControls } = wp.blockEditor;
const { useState, useEffect } = wp.element;
const { __ } = wp.i18n;

registerBlockType('webask/survey-block', {
    title: __('WebAsk Survey', 'webask-integration'),
    description: __('Embed a WebAsk.io survey', 'webask-integration'),
    icon: 'feedback',
    category: 'embed',
    
    attributes: {
        surveyId: {
            type: 'string',
            default: ''
        },
        width: {
            type: 'string',
            default: '100%'
        },
        height: {
            type: 'string', 
            default: '600px'
        },
        style: {
            type: 'string',
            default: 'iframe'
        }
    },
    
    edit: function(props) {
        const { attributes, setAttributes } = props;
        const { surveyId, width, height, style } = attributes;
        
        const [surveys, setSurveys] = useState([]);
        const [loading, setLoading] = useState(true);
        const [surveyData, setSurveyData] = useState(null);
        
        // Load surveys list on component mount
        useEffect(() => {
            loadSurveys();
        }, []);
        
        // Load specific survey data when surveyId changes
        useEffect(() => {
            if (surveyId) {
                loadSurveyData(surveyId);
            } else {
                setSurveyData(null);
            }
        }, [surveyId]);
        
        function loadSurveys() {
            fetch(webask_gutenberg.ajax_url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'webask_get_surveys',
                    nonce: webask_gutenberg.nonce
                })
            })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    const surveyOptions = [
                        { value: '', label: __('Select a survey...', 'webask-integration') }
                    ];
                    
                    data.data.forEach(survey => {
                        if (survey.is_published) {
                            surveyOptions.push({
                                value: survey.id.toString(),
                                label: survey.name + ' (ID: ' + survey.id + ')'
                            });
                        }
                    });
                    
                    setSurveys(surveyOptions);
                }
                setLoading(false);
            })
            .catch(error => {
                console.error('Error loading surveys:', error);
                setLoading(false);
            });
        }
        
        function loadSurveyData(id) {
            fetch(webask_gutenberg.ajax_url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'webask_get_survey_data',
                    survey_id: id,
                    nonce: webask_gutenberg.nonce
                })
            })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    setSurveyData(data.data);
                }
            })
            .catch(error => {
                console.error('Error loading survey data:', error);
            });
        }
        
        return (
            <>
                <InspectorControls>
                    <PanelBody title={__('Survey Settings', 'webask-integration')}>
                        <SelectControl
                            label={__('Survey', 'webask-integration')}
                            value={surveyId}
                            options={surveys}
                            onChange={(value) => setAttributes({ surveyId: value })}
                            disabled={loading}
                        />
                        
                        <TextControl
                            label={__('Width', 'webask-integration')}
                            value={width}
                            onChange={(value) => setAttributes({ width: value })}
                            placeholder="100%"
                            help={__('e.g., 100%, 800px', 'webask-integration')}
                        />
                        
                        <TextControl
                            label={__('Height', 'webask-integration')}
                            value={height}
                            onChange={(value) => setAttributes({ height: value })}
                            placeholder="600px"
                            help={__('e.g., 600px, 50vh', 'webask-integration')}
                        />
                        
                        <SelectControl
                            label={__('Display Style', 'webask-integration')}
                            value={style}
                            options={[
                                { value: 'iframe', label: __('iFrame', 'webask-integration') },
                                { value: 'embed', label: __('Direct Embed', 'webask-integration') }
                            ]}
                            onChange={(value) => setAttributes({ style: value })}
                        />
                    </PanelBody>
                </InspectorControls>
                
                <div className="webask-survey-block-editor">
                    {loading && (
                        <div className="webask-loading">
                            {__('Loading surveys...', 'webask-integration')}
                        </div>
                    )}
                    
                    {!loading && !surveyId && (
                        <div className="webask-placeholder">
                            <div className="webask-icon">📊</div>
                            <h3>{__('WebAsk Survey', 'webask-integration')}</h3>
                            <p>{__('Select a survey from the sidebar to get started.', 'webask-integration')}</p>
                        </div>
                    )}
                    
                    {surveyId && !surveyData && (
                        <div className="webask-loading">
                            {__('Loading survey data...', 'webask-integration')}
                        </div>
                    )}
                    
                    {surveyId && surveyData && (
                        <div className="webask-preview">
                            <div className="webask-preview-header">
                                <strong>{surveyData.name}</strong>
                                <span className="webask-survey-id">ID: {surveyId}</span>
                            </div>
                            <div className="webask-preview-content">
                                <iframe
                                    src={surveyData.url_shared}
                                    width={width}
                                    height={height}
                                    frameBorder="0"
                                    style={{ border: 'none', maxWidth: '100%' }}
                                />
                            </div>
                            <div className="webask-preview-footer">
                                <small>
                                    {__('Preview - Survey will be embedded in frontend', 'webask-integration')}
                                </small>
                            </div>
                        </div>
                    )}
                </div>
            </>
        );
    },
    
    save: function(props) {
        const { attributes } = props;
        const { surveyId, width, height, style } = attributes;
        
        if (!surveyId) {
            return null;
        }
        
        // Return shortcode that will be processed by WordPress
        return (
            <div className="wp-block-webask-survey">
                {`[webask_survey id="${surveyId}" width="${width}" height="${height}" style="${style}"]`}
            </div>
        );
    }
});